import { Button, ModalWindow } from '@cian/ui-kit';
import * as React from 'react';
import { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { TFormSubmitStatus, TFormValues, TSubmitStatus } from './types';
import { SuccessMessage } from './components';
import { ConsultationFormControls } from './components/ConsultationFormControls/ConsulttionFormControls';
import { TThunkDispatch } from '../../types/redux';
import { hasErrors, validateEmailFormInputs, validatePhoneFormInputs } from './utils';
import { PHONE_ADORNMENT } from './constants';
import { NBSP } from '../../constants/symbols';
import { useActiveModalComponent } from './utils/useActiveModalComponent';
import { selectActiveStep } from '../../selectors/consultationRequest';
import { sendConsultationRequest, setActiveStep } from '../../actions/consultationRequest';
import { EActiveConsultationModal } from '../../types/redux/states';

interface IConsultationRequestModalContainer {
  requestSentFrom: string;
}

export const ConsultationRequestModalContainer: React.FC<IConsultationRequestModalContainer> = ({
  requestSentFrom,
}) => {
  const dispatch = useDispatch<TThunkDispatch>();
  const activeStep = useSelector(selectActiveStep);

  const initialValue: TFormValues = {
    email: '',
    question: '',
    sentFrom: requestSentFrom,
  };
  const [values, setValues] = useState<TFormValues>(initialValue);
  const [submitStatus, setSubmitStatus] = useState<TFormSubmitStatus>({});

  const submitDisable = submitStatus.status === TSubmitStatus.Loading || !!submitStatus.errors;

  const handleChangeInputValue = <T extends keyof TFormValues>(inputName: T, value: TFormValues[T]) => {
    setValues({ ...values, [inputName]: value });
    setSubmitStatus({ ...submitStatus, errors: null });
  };

  const currentActiveModal = useActiveModalComponent({
    handleChangeInputValue,
    values,
    submitStatus,
  });

  const onCancel = () => {
    dispatch(setActiveStep(null));
    setValues(initialValue);
    setSubmitStatus({});
  };

  const handleValidation = () => {
    const validationErrors =
      activeStep === EActiveConsultationModal.PhoneForm
        ? validatePhoneFormInputs(values)
        : validateEmailFormInputs(values);

    if (hasErrors(validationErrors)) {
      return { status: TSubmitStatus.Error, errors: validationErrors };
    }

    return { errors: null, status: TSubmitStatus.Loading };
  };

  const onSubmit = async () => {
    setSubmitStatus({ errors: null, status: TSubmitStatus.Loading });

    const params =
      activeStep === EActiveConsultationModal.PhoneForm
        ? { ...values, phone: `${PHONE_ADORNMENT} ${values.phone}`, email: undefined }
        : { ...values };

    const result = await dispatch(sendConsultationRequest(params));
    setSubmitStatus(result);
  };

  const handleSubmit = async () => {
    const validation = handleValidation();
    setSubmitStatus(validation);

    if (validation.errors) {
      return;
    }

    await onSubmit();
  };

  return (
    <ModalWindow
      open={!!activeStep}
      onClose={onCancel}
      content={
        submitStatus.status === TSubmitStatus.Submit ? (
          <SuccessMessage
            onClose={onCancel}
            message={
              activeStep === EActiveConsultationModal.EmailForm
                ? `Пришлём ответ в${NBSP}письме${NBSP}в${NBSP}ближайшее время`
                : `Перезвоним вам${NBSP}в${NBSP}ближайшее время`
            }
          />
        ) : (
          currentActiveModal
        )
      }
      width={540}
      footer={
        activeStep !== EActiveConsultationModal.ChoosingConsultationType &&
        submitStatus.status !== 'submit' && (
          <ConsultationFormControls
            controls={
              <>
                <Button
                  onClick={handleSubmit}
                  size="L"
                  disabled={submitDisable}
                  test-id="leave-contacts-modal-submit-button"
                >
                  {activeStep === EActiveConsultationModal.EmailForm ? 'Отправить' : 'Перезвоните мне'}
                </Button>
                <Button onClick={onCancel} size="L" theme="fill_white_primary">
                  Отменить
                </Button>
              </>
            }
          />
        )
      }
    />
  );
};
